/**
 * Enhanced API Client with JWT Token Management and Automatic Refresh
 * 
 * Features:
 * - Automatic addition of Authorization header with Bearer token
 * - Token refresh on 401 errors for specific endpoints
 * - Request queuing during token refresh
 * - Support for both wrapped and unwrapped API responses
 */

import axios from 'axios';
import tokenManager from './tokenManager';
import API_BASE_URL from './apiConfig'; 

const SESSION_EXPIRED_EVENT = 'auth:session-expired';

const notifySessionExpired = (reason = 'session_expired') => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(SESSION_EXPIRED_EVENT, {
        detail: { reason },
      })
    );
  }
};

const hadAuthenticatedContext = (requestConfig) => {
  const inMemoryToken = tokenManager.getAuthToken();
  const inMemoryUserId = tokenManager.getUserId();
  const authHeader = requestConfig?.headers?.Authorization || requestConfig?.headers?.authorization;

  return Boolean(inMemoryToken || inMemoryUserId || authHeader);
};

// Refresh token endpoint (full URL as per guide)
const REFRESH_TOKEN_URL = `${API_BASE_URL}/auth/refresh`; 

// Flag to track if a token refresh is in progress
let isRefreshing = false;

// Queue to hold requests while token is being refreshed
let failedQueue = [];

/**
 * Process queued requests after token refresh
 * @param {Error|null} error - Error if refresh failed
 * @param {string|null} token - New token if refresh succeeded
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

/**
 * Refresh the access token using the refresh token cookie
 * @returns {Promise<string|null>} New access token or null if refresh failed
 */
export const refreshAccessToken = async () => {
  try {
    
    const response = await axios.post(
      REFRESH_TOKEN_URL,
      {}, // Empty body
      {
        withCredentials: true, // Include refreshToken cookie
      }
    );

    // Handle multiple possible response structures
    const responseData = response.data?.data || response.data;
    const newToken = responseData?.accessToken || 
                     response.data?.accessToken || 
                     responseData?.token || 
                     response.data?.token;

    if (newToken) {
      tokenManager.setAuthToken(newToken);
      
      // Extract userId with fallback logic
      const userId = responseData?.id || 
                     response.data?.id || 
                     responseData?.userId || 
                     responseData?.uid ||
                     responseData?.user?.id;

      if (userId) {
        tokenManager.setUserId(userId.toString());
      } else {
        // Fallback: fetch userId from /auth/me endpoint
        try {
          const meResponse = await axios.get(`${API_BASE_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${newToken}` }
          });
          const meUserId = meResponse.data?.id || meResponse.data?.userId;
          if (meUserId) {
            tokenManager.setUserId(meUserId.toString());
          }
        } catch (meError) {
          console.warn('⚠️ Could not fetch user ID from /auth/me');
        }
      }

      // Extract user role if available
      const userRole = responseData?.role || 
                       response.data?.role || 
                       responseData?.userRole;
      if (userRole) {
        tokenManager.setUserRole(userRole);
      }

      return newToken;
    }

    console.error('No token in refresh response');
    return null;
  } catch (error) {
    console.error('Token refresh failed:', error.message);
    tokenManager.clear();
    return null;
  }
};

/**
 * Create axios instance with base configuration
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 120 seconds for long operations
  withCredentials: true, // Enable sending cookies with requests
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Endpoints that should trigger automatic token refresh on 401
 */
const refreshableEndpoints = [
  '/petBiometrics/verifyPet',
  '/petBiometrics/register',
  '/pets/create-pet-basic-info-with-image',
  '/pets/pet-list/',
  '/pets/',  // Covers /pets/{id}/basic-info
  '/account/', // User profile endpoints
];

/**
 * Check if endpoint should trigger token refresh
 * @param {string} url - Request URL
 * @returns {boolean} True if endpoint is refreshable
 */
const isRefreshableEndpoint = (url) => {
  if (!url) return false;
  return refreshableEndpoints.some(endpoint => url.includes(endpoint));
};

/**
 * Request Interceptor
 * Automatically adds Authorization header and handles FormData
 */
apiClient.interceptors.request.use(
  (config) => {
    const token = tokenManager.getAuthToken();
    
    // Add Authorization header if token exists
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Handle FormData - let axios set the correct multipart boundary
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers && config.headers['Content-Type']) {
        delete config.headers['Content-Type'];
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handles token refresh on 401 errors and error formatting
 */
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const shouldShowSessionExpiredPopup = hadAuthenticatedContext(originalRequest);

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Only refresh for specific endpoints
      if (isRefreshableEndpoint(originalRequest.url)) {
        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers['Authorization'] = 'Bearer ' + token;
              return apiClient(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();
          
          if (newToken) {
            // Update the failed request with new token
            originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
            
            // Process queued requests
            processQueue(null, newToken);
            
            // Retry the original request
            return apiClient(originalRequest);
          } else {
            // Refresh failed, clear tokens
            processQueue(new Error('Token refresh failed'), null);
            tokenManager.clear();
            if (shouldShowSessionExpiredPopup) {
              notifySessionExpired('refresh_failed');
            }
            
            // Optionally redirect to login
            // window.location.href = '/login';
            
            return Promise.reject(error);
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          tokenManager.clear();
          if (shouldShowSessionExpiredPopup) {
            notifySessionExpired('refresh_exception');
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        // For non-refreshable endpoints, just clear tokens
        tokenManager.clear();
        if (shouldShowSessionExpiredPopup) {
          notifySessionExpired('unauthorized');
        }
      }
    }

    // Handle other errors
    if (error.response) {
      const errorDetails = {
        status: error.response.status,
        url: error.config?.url || 'unknown',
        method: error.config?.method?.toUpperCase() || 'unknown',
        data: error.response.data
      };

      console.error(`API Error ${error.response.status}:`, errorDetails);
    } else if (error.request) {
      console.error('Network Error:', error.message);
    } else {
      console.error('Error:', error.message);
    }

    return Promise.reject(error);
  }
);

/**
 * Helper function to check if response is wrapped
 * @param {any} data - Response data
 * @returns {boolean} True if response is wrapped
 */
function isApiResponse(data) {
  return data && typeof data === 'object' && 'data' in data && 'success' in data;
}

/**
 * Helper function to unwrap API response
 * @param {any} responseData - Response data
 * @returns {any} Unwrapped data
 */
function unwrapResponse(responseData) {
  return isApiResponse(responseData) ? responseData.data : responseData;
}

/**
 * GET request helper
 * @param {string} url - Request URL
 * @param {object} config - Axios config
 * @returns {Promise<any>} Response data
 */
export const get = async (url, config = {}) => {
  const response = await apiClient.get(url, config);
  return unwrapResponse(response.data);
};

/**
 * POST request helper
 * @param {string} url - Request URL
 * @param {any} data - Request body
 * @param {object} config - Axios config
 * @returns {Promise<any>} Response data
 */
export const post = async (url, data, config = {}) => {
  const response = await apiClient.post(url, data, config);
  return unwrapResponse(response.data);
};

/**
 * PUT request helper
 * @param {string} url - Request URL
 * @param {any} data - Request body
 * @param {object} config - Axios config
 * @returns {Promise<any>} Response data
 */
export const put = async (url, data, config = {}) => {
  const response = await apiClient.put(url, data, config);
  return unwrapResponse(response.data);
};

/**
 * DELETE request helper
 * @param {string} url - Request URL
 * @param {object} config - Axios config
 * @returns {Promise<any>} Response data
 */
export const del = async (url, config = {}) => {
  const response = await apiClient.delete(url, config);
  return unwrapResponse(response.data);
};

/**
 * GET request with status code
 * @param {string} url - Request URL
 * @param {object} config - Axios config
 * @returns {Promise<{data: any, statusCode: number, statusText: string}>}
 */
export const getWithStatus = async (url, config = {}) => {
  const response = await apiClient.get(url, config);
  return {
    data: unwrapResponse(response.data),
    statusCode: response.status,
    statusText: response.statusText
  };
};

/**
 * POST request with status code
 * @param {string} url - Request URL
 * @param {any} data - Request body
 * @param {object} config - Axios config
 * @returns {Promise<{data: any, statusCode: number, statusText: string}>}
 */
export const postWithStatus = async (url, data, config = {}) => {
  const response = await apiClient.post(url, data, config);
  return {
    data: unwrapResponse(response.data),
    statusCode: response.status,
    statusText: response.statusText
  };
};

/**
 * PUT request with status code
 * @param {string} url - Request URL
 * @param {any} data - Request body
 * @param {object} config - Axios config
 * @returns {Promise<{data: any, statusCode: number, statusText: string}>}
 */
export const putWithStatus = async (url, data, config = {}) => {
  const response = await apiClient.put(url, data, config);
  return {
    data: unwrapResponse(response.data),
    statusCode: response.status,
    statusText: response.statusText
  };
};

/**
 * DELETE request with status code
 * @param {string} url - Request URL
 * @param {object} config - Axios config
 * @returns {Promise<{data: any, statusCode: number, statusText: string}>}
 */
export const deleteWithStatus = async (url, config = {}) => {
  const response = await apiClient.delete(url, config);
  return {
    data: unwrapResponse(response.data),
    statusCode: response.status,
    statusText: response.statusText
  };
};

export default apiClient;
